import React from "react";
import { Box, Typography, Button, IconButton, Chip } from "@mui/material";
import { ArrowBackRounded, LogoutRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { demo } from "./demoStyles";

// Shared wrapper for every demo sub-page.
// Renders the sticky top-bar, a content area, and a "Demo Data" badge.
const DemoShell = ({ title, icon: Icon, gradient, children }) => {
  const navigate = useNavigate();
  const { clearTokens } = useAuth();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: demo.bgGradient,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Sticky top-bar ──────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, md: 4 },
          py: 1.5,
          borderBottom: `1px solid ${demo.border}`,
          position: "sticky",
          top: 0,
          zIndex: 100,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(7,11,20,0.60)",
        }}
      >
        <IconButton
          onClick={() => navigate("/demo")}
          size="small"
          sx={{
            color: demo.textDim,
            border: `1px solid ${demo.border}`,
            borderRadius: "8px",
            p: 0.6,
            "&:hover": { color: demo.text, borderColor: demo.borderStrong, background: demo.surface },
          }}
        >
          <ArrowBackRounded fontSize="small" />
        </IconButton>

        {/* Icon + title */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: gradient || `linear-gradient(135deg, ${demo.primary}, ${demo.accent})`,
            boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
            flexShrink: 0,
          }}
        >
          {Icon && <Icon sx={{ color: "#fff", fontSize: 20 }} />}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: demo.textDim, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", lineHeight: 1 }}>
            SERVOTECH ONE
          </Typography>
          <Typography
            sx={{ color: demo.text, fontWeight: 700, fontSize: 15.5, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {title}
          </Typography>
        </Box>

        {/* Demo badge */}
        <Chip
          label="Demo Data"
          size="small"
          sx={{
            height: 22,
            fontSize: 10.5,
            fontWeight: 700,
            color: demo.amber,
            background: "rgba(251,191,36,0.10)",
            border: "1px solid rgba(251,191,36,0.25)",
            "& .MuiChip-label": { px: 1 },
            display: { xs: "none", sm: "flex" },
          }}
        />

        <Button
          onClick={() => { clearTokens(); navigate("/demo/login"); }}
          startIcon={<LogoutRounded fontSize="small" />}
          sx={{
            color: demo.textDim,
            textTransform: "none",
            fontSize: 13,
            borderRadius: "9px",
            border: `1px solid ${demo.border}`,
            px: 1.6,
            py: 0.6,
            "&:hover": { color: demo.text, borderColor: demo.borderStrong, background: demo.surface },
          }}
        >
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Sign out</Box>
        </Button>
      </Box>

      {/* ── Page content ────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2.5, md: 3.5 },
          maxWidth: 1280,
          mx: "auto",
          width: "100%",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default DemoShell;
